import { useState } from 'react';
import { format } from 'date-fns';
import ArticleModal from './ArticleModal';
import InterstitialAd from './InterstitialAd';
import { Clock, Circle } from 'lucide-react';

const TimelineView = ({ story }) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [interstitialUrl, setInterstitialUrl] = useState('');

  const articles = story.articles || [];

  const handleEventClick = (article) => {
    setSelectedArticle(article);
  };

  const handleReadFullStory = (url) => {
    setInterstitialUrl(url);
    setSelectedArticle(null);
    setShowInterstitial(true);
  };

  const handleCloseInterstitial = () => {
    setShowInterstitial(false);
    setInterstitialUrl('');
  };

  const generateEventSummary = (title, description) => {
    const text = description || title;
    const words = text.split(' ');
    const summary = words.slice(0, 30).join(' ');
    return summary.length > 150 ? summary.substring(0, 150) + '...' : summary;
  };

  return (
    <div className="mb-8 sm:mb-12" data-testid="timeline-view">
      {/* Story Header */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-serif font-bold text-gray-900 mb-1 sm:mb-2 leading-tight">
          {story.primary_headline}
        </h2>
        <p className="text-xs sm:text-sm text-gray-600">
          Developing story • {articles.length} updates
        </p>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-0.5 bg-blue-200" />

        {/* Timeline events */}
        <div className="space-y-6 sm:space-y-8">
          {articles.map((article, index) => {
            const isClarity = !!article.is_clarity_pick;
            const date = article.pub_date ? new Date(article.pub_date) : new Date();
            const summary = generateEventSummary(article.title, article.description);

            return (
              <div
                key={article.id}
                className="relative pl-16 sm:pl-20 group"
                data-testid="timeline-event"
              >
                {/* Date and time marker */}
                <div className="absolute left-0 flex items-center">
                  <div className="flex flex-col items-end mr-3 sm:mr-4 text-right">
                    <span className="text-xs sm:text-sm font-semibold text-gray-900">
                      {format(date, 'MMM d')}
                    </span>
                    <span className="text-xs text-gray-500">
                      {format(date, 'HH:mm')}
                    </span>
                  </div>
                  
                  {/* Timeline dot */}
                  <div
                    className={`relative z-10 flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 ${
                      isClarity
                        ? 'bg-blue-600 border-blue-600'
                        : 'bg-white border-blue-300 group-hover:border-blue-500'
                    }`}
                  >
                    {isClarity ? (
                      <Circle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white fill-current" />
                    ) : (
                      <Circle className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-blue-300 fill-current group-hover:text-blue-500" />
                    )}
                  </div>
                </div>

                {/* Event card */}
                <div
                  onClick={() => handleEventClick(article)}
                  className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all active:scale-[0.99]"
                >
                  {isClarity && (
                    <div className="mb-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                        <Clock className="w-3 h-3" />
                        Clarity's Pick
                      </span>
                    </div>
                  )}

                  <h3 className="font-semibold text-sm sm:text-base text-gray-900 mb-2 line-clamp-2">
                    {article.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-gray-700 mb-3 leading-relaxed line-clamp-3">
                    {summary}
                  </p>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="font-medium">{article.source.name}</span>
                    <span className="text-gray-400">Tap to read →</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Article Modal */}
      {selectedArticle && (
        <ArticleModal
          article={selectedArticle}
          isOpen={!!selectedArticle}
          onClose={() => setSelectedArticle(null)}
          onReadFullStory={handleReadFullStory}
        />
      )}

      {/* Interstitial Ad */}
      {showInterstitial && (
        <InterstitialAd
          targetUrl={interstitialUrl}
          onClose={handleCloseInterstitial}
          autoRedirectDelay={5}
        />
      )}
    </div>
  );
};

export default TimelineView;
